import IErrorHandler from "./IErrorHandler";
import events from "../events/EventsObservable";
import { ERROR_EVENTS } from "../connect/controllers/ConnectControl";
import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import { STATES } from "../states/states";
import Messages from "../Messages";
import {
  ERROR_CATEGORIES,
  ERROR_CODES,
  getErrorCategoryByCode,
} from "./errorCodes";
import Wallet from "../wallet/Wallet";
import MadmaxError, { errorFactroy } from "./MadmaxError";
import IGame from "../game/IGame";
import IStatesManager from "../states/IStatesManager";
import { GAME_EVENTS } from "../game/gameEvents";
import PostMessages from "../system/PostMessages";
import Freespins from "../wallet/Freespins";

@injectable()
export default class ErrorHandler implements IErrorHandler {
  private static readonly DEFAULT_SKIP_CODES = new Set<number>([
    ERROR_CODES.BET_ROLLED_BACK,
    ERROR_CODES.HOUR_LOSS_LIMIT,
    ERROR_CODES.DAILY_LOSS_LIMIT,
    ERROR_CODES.WEEK_LOSS_LIMIT,
    ERROR_CODES.MONTH_LOSS_LIMIT,
    ERROR_CODES.DAILY_BET_LIMIT,
    ERROR_CODES.WEEKLY_BET_LIMIT,
    ERROR_CODES.MONTHLY_BET_LIMIT,
    ERROR_CODES.COOLDOWN,
    ERROR_CODES.LIMIT_IS_MISSING,
    ERROR_CODES.WITHDRAW_LIMIT,
    ERROR_CODES.PLAYER_LIMIT_EXCEEDED,
  ]);

  private states: IStatesManager;
  private messages: Messages;
  private game: IGame;
  private wallet: Wallet;
  private postMessages: PostMessages;
  private freespins: Freespins;
  private _skipOnError: null | number[] | "all" = null;

  constructor(
    @inject(SERVICE_IDENTIFIER.StatesManager) statesManager: IStatesManager,
    @inject(SERVICE_IDENTIFIER.SystemMessages) messages: Messages,
    @inject(SERVICE_IDENTIFIER.Game) game: IGame,
    @inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
    @inject(SERVICE_IDENTIFIER.PostMessages) postMessages: PostMessages,
    @inject(SERVICE_IDENTIFIER.Freespins) freespins: Freespins,
  ) {
    this.states = statesManager;
    this.messages = messages;
    this.game = game;
    this.wallet = wallet;
    this.postMessages = postMessages;
    this.freespins = freespins;

    // increment round id
    events.subscribe(GAME_EVENTS.GAME_SKIP_ROUND_ID, async () => {
      this.game.round.skipId();
      if (this.game.round.getSkippedIds() > 3) {
        this.handleError(errorFactroy({ code: ERROR_CODES.WALLET_SERVER_EXC }));
      }
    });
  }

  async handleError(error) {
    if (!(error instanceof MadmaxError)) {
      throw error;
      return;
    }

    console.error(error);

    // SEND IFORIUM POST MESSAGE
    if (error.window_messages && this.postMessages.isActive()) {
      this.postMessages.handleWalletMessage(error.window_messages);
    }

    if (this._shouldSkipRound(error.code)) {
      this.skipRound();
    }

    switch (error.code) {
      case ERROR_CODES.GAME_NOT_ENOUGH_CREDIT:
        this.states.setState(STATES.IDLE);
        this._showErrorMessage(error);
        return;
      case ERROR_CODES.INSUFFICIENT_CREDIT:
        if (this.wallet.isBonus()) {
          this.freespins.showContinueMessage();
        }
        break;
    }

    let errorCategory = getErrorCategoryByCode(error.code);

    switch (errorCategory) {
      case ERROR_CATEGORIES.FATAL_STOP_LOGOUT:
        this.fatalStopLogout(error);
        break;

      case ERROR_CATEGORIES.ROLLBACK:
        this.rollback(error);
        break;

      case ERROR_CATEGORIES.ROLLBACK_FATAL:
        this.rollbackFatal(error);
        break;

      case ERROR_CATEGORIES.COOLDOWN:
        this.cooldownError(error);
        break;

      case ERROR_CATEGORIES.ROUND_ENDED:
        this.roundEnded(error);
        break;

      case ERROR_CATEGORIES.REDIRECT:
        this.redirect(error);
        break;

      default:
        this.defaultError(error);
    }

    if (
      error.code === ERROR_CODES.FREESPINS_EXPIRED ||
      error.code === ERROR_CODES.FREESPINS_ALREADY_REDEEMED
    ) {
      this.wallet.setFreeSpins(0);
    }
  }

  configure(skipOnError: null | number[] | "all"): void {
    this._skipOnError = skipOnError;
  }

  skipRound() {
    this.game.round.skipId();
    if (this.game.round.getSkippedIds() > 3) {
      this.handleError(errorFactroy({ code: ERROR_CODES.WALLET_SERVER_EXC }));
    }
  }

  private _shouldSkipRound(code: number): boolean {
    if (this._skipOnError === "all") return true;
    if (ErrorHandler.DEFAULT_SKIP_CODES.has(code)) return true;
    return Array.isArray(this._skipOnError) && this._skipOnError.includes(code);
  }

  fatalStopLogout(error) {
    events.dispatch(ERROR_EVENTS.ERROR_LOGOUT);
    this.states.setState(STATES.ERROR, { error: error });
    this._showErrorMessage(error);
  }

  rollback(error) {
    // events.dispatch(ERROR_EVENTS.ERROR_BALANCE_REFUND, roundData);
    this._refund();

    this.states.setState(STATES.ERROR);

    let errorMessge = this._showErrorMessage(error);
    errorMessge.setOnClose(() => {
      this.states.setState(STATES.IDLE);
      errorMessge.unsetOnClose();
    });
  }

  rollbackFatal(error?, roundData?) {
    // events.dispatch(ERROR_EVENTS.ERROR_BALANCE_REFUND, roundData);
    this._refund();

    this.states.setState(STATES.ERROR);

    this._showErrorMessage(error);
  }

  cooldownError(error?, roundData?) {
    // events.dispatch(ERROR_EVENTS.ERROR_BALANCE_REFUND, roundData);
    this._refund();
    events.dispatch(ERROR_EVENTS.ERROR_REGULATION_COOLDOWN, error);
  }

  roundEnded(error?, roundData?, params?): boolean {
    this._showErrorMessage(error);
    return false;
  }

  defaultError(error?, roundData?) {
    this._showErrorMessage(error);
  }

  redirect(error: MadmaxError) {
    if (error?.error?.response.data.redirect) {
      window.location.href = error.error.response.data.redirect;
    }
  }

  private _showErrorMessage(madmaxError) {
    let errorMessge = this.messages.getErrorMessage(madmaxError);
    errorMessge.unsetOnClose();
    if (!this.postMessages.isActive() || !madmaxError?.window_messages) {
      errorMessge.openMessage();
    }
    return errorMessge;
  }

  private _refund() {
    let balance = this.game.round.getBalanceTmp();
    if (balance != null) {
      this.wallet.refund(balance);
    }

    this.game.round.setBalanceTmp(null);
  }
}
